import * as Yup from "yup";
import { Box } from "@mui/system";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router";
import ObservationForm from "./Observation";
import { useEffect, useState } from "react";
import { IconPlus, IconX } from "@tabler/icons";
import { FieldArray, Form, Formik } from "formik";
import { configState } from "../../../recoil/atoms";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import useAuthorization from "../../Authentication/permissions/Authorization";
import { Button, Divider, Grid, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import {
  AddObservationRequest,
  AppointmentApi,
  EditObservationRequest,
  GetAppointmentResponse,
  ObservationApi,
  Tests,
  TestsApi,
} from "../../../api/medical-operations";

const Observations = () => {
  const { appointmentID } = useParams();
  const config = useRecoilValue(configState);

  const [tests, setTests] = useState<Tests[]>([]);
  const [isClosed, setIsClosed] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [appointment, setAppointment] = useState<GetAppointmentResponse>();

  const { canEditAppointmentObservation, canDeleteAppointmentObservation, canAddAppointmentObservation } = useAuthorization();

  const deleteObservation = (obsNb: number) => {
    const Observation = new ObservationApi(config);
    Observation.deleteAppointmentObservation(appointmentID!, obsNb)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const addObservation = (values: AddObservationRequest) => {
    const Observation = new ObservationApi(config);
    Observation.addAppointmentObservation(appointmentID!, values)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const editObservation = (obsNb: number, values: EditObservationRequest) => {
    const editedValues = {
      Feedback: values?.Feedback,
      RequestedTests: values?.RequestedTests,
      Symptoms: values?.Symptoms,
    };
    const Observation = new ObservationApi(config);
    Observation.editAppointmentObservation(obsNb, appointmentID!, editedValues)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const getAppointment = () => {
    const appointment = new AppointmentApi(config);
    appointment
      .getAppointmentById(appointmentID!)
      .then((res) => {
        setIsLoading(false);
        setAppointment(res.data);
        setIsClosed(res.data.Status);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const getTests = () => {
    const Tests = new TestsApi(config);
    Tests.getTests()
      .then((res) => {
        setTests(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getAppointment();
    getTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MainCard>
        {!isLoading ? (
          <>
            <Formik
                initialValues={appointment!}
                enableReinitialize={true}
                validationSchema={Yup.object().shape({
                    Observations: Yup.array().of(
                        Yup.object().shape({
                          Symptoms: Yup.array().required("Required").of(
                              Yup.string().trim().matches(/^[a-zA-Z0-9\s]+$/,"Incorrect format")
                          ).min(1,"At least one symptom required"),
                          RequestedTests: Yup.array().required("Required").of(
                              Yup.string().trim().matches(/^[a-zA-Z0-9\s]+$/,"Incorrect format").min(1,"At least one test required")
                          ),
                          Feedback: Yup.array().required("Required").of(
                              Yup.string().trim().matches(/^[a-zA-Z0-9\s]+$/,"Incorrect format").min(1,"Feedback is required")
                          ),
                        })
                    )
                })}
                onSubmit={() => {}}>
              {({ isValid, dirty, values}) =>
                (
                  <Form>
                    <Box sx={{ width: "100%" }}>
                      <Box sx={{ bgcolor: "#fff", p: 1 }}>
                        <Typography variant="h3" gutterBottom sx={{ p: 0 }}>
                          Appointment Observations
                        </Typography>
                        <Divider />
                        <Grid
                          sx={{
                            justifyContent: "center",
                            pt: 4,  
                            pb: 4
                          }}
                          width="100%"
                        >
                          <FieldArray
                            name={`Observations`}
                            render={(arrayHelpers) => (
                              <>
                                {canAddAppointmentObservation && isClosed !== "Closed" && (
                                  <Grid
                                    container
                                    sx={{
                                      justifyContent: "right",
                                      alignContent: "right",
                                    }}
                                  >
                                    <Grid item>
                                      <Button
                                        onClick={() => {
                                          arrayHelpers.push("");
                                        }}
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ p: 1, mb: 2 }}
                                      >
                                        <IconPlus />
                                        Add Observation
                                      </Button>
                                    </Grid>
                                  </Grid>
                                )}

                                <Grid container spacing={6}>
                                  {values?.Observations?.map((observation: any, index: number) => (
                                    <>
                                      <Grid item xs={12} sm={4} md={6} key={"Observation" + index}>
                                        <Grid
                                          container
                                          sx={{
                                            justifyContent: "center",
                                            alignContent: "center",
                                            bgcolor: "#e7e3ff",
                                            color: "grey.800",
                                            borderRadius: "16px",
                                            display: "flex",
                                            flexDirection: "row",
                                            p: 1,
                                          }}
                                        >
                                          {canDeleteAppointmentObservation && (
                                            <Grid item xs={1} sm={1} sx={{ marginLeft: "auto", mr: 2 }}>
                                              <Tooltip title="Delete">
                                                <IconButton
                                                  onClick={() => {
                                                    arrayHelpers.remove(index);
                                                    deleteObservation(observation?.ObservationNb);
                                                  }}
                                                >
                                                  <IconX color="red" />
                                                </IconButton>
                                              </Tooltip>
                                            </Grid>
                                          )}
                                          <Grid container />
                                          <Grid item xs={12} sm={12}>
                                            <ObservationForm index={index} tests={tests} />
                                          </Grid>
                                          <Grid container />
                                          {canEditAppointmentObservation && (
                                            <Grid item xs={1} sm={2} sx={{ m: 1 }}>
                                              <Button
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                fullWidth
                                                onClick={() => {
                                                   isValid && (observation?.ObservationNb
                                                    ? editObservation(observation?.ObservationNb, observation)
                                                    : addObservation([observation]));
                                                }}
                                              >
                                                Save
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </>
                                  ))}
                                </Grid>
                              </>
                            )}
                          />
                        </Grid>
                      </Box>
                    </Box>
                  </Form>
                )
              }
            </Formik>
          </>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    </>
  );
};

export default Observations;
